import { useState, useEffect } from 'react';
import { lendersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';
import { BulkSelectCheckbox, BulkActionBar } from '@/components/BulkActions';

export default function AdminLenders() {
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', contact_name: '', contact_email: '', contact_phone: '', requirements: '', notes: '', specialties: '' });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const load = () => lendersAPI.list().then(r => { setLenders(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openEdit = (l) => {
    setForm({ ...l, specialties: (l.specialties || []).join(', ') });
    setEditId(l.id);
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, specialties: form.specialties.split(',').map(s => s.trim()).filter(Boolean) };
      if (editId) {
        await lendersAPI.update(editId, payload);
        toast.success('Lender updated');
      } else {
        await lendersAPI.create(payload);
        toast.success('Lender added');
      }
      setDialogOpen(false);
      setEditId(null);
      setForm({ name: '', address: '', contact_name: '', contact_email: '', contact_phone: '', requirements: '', notes: '', specialties: '' });
      load();
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this lender?')) return;
    try { await lendersAPI.delete(id); toast.success('Removed'); load(); } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-lenders">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 uppercase">Lender Management</h1>
        <Button className="bg-[#E31937] text-white hover:bg-[#C4122C]" onClick={() => { setEditId(null); setForm({ name: '', contact_name: '', contact_email: '', contact_phone: '', requirements: '', notes: '', specialties: '' }); setDialogOpen(true); }} data-testid="add-lender-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Lender
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead className="w-10"><BulkSelectCheckbox checked={selected.length === lenders.length && lenders.length > 0} onCheckedChange={() => setSelected(p => p.length === lenders.length ? [] : lenders.map(l => l.id))} testId="select-all-lenders" /></TableHead>
              <TableHead className="text-gray-500">Lender Name</TableHead>
              <TableHead className="text-gray-500 hidden sm:table-cell">Contact</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell">Email</TableHead>
              <TableHead className="text-gray-500 hidden lg:table-cell">Specialties</TableHead>
              <TableHead className="text-gray-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">Loading...</TableCell></TableRow>
            ) : lenders.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">No lenders added yet</TableCell></TableRow>
            ) : lenders.map(l => (
              <TableRow key={l.id} className="border-gray-200 text-gray-700">
                <TableCell><BulkSelectCheckbox checked={selected.includes(l.id)} onCheckedChange={() => toggleSelect(l.id)} testId={`select-lender-${l.id}`} /></TableCell>
                <TableCell className="font-medium"><div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-gray-400 hidden sm:block" />{l.name}</div></TableCell>
                <TableCell className="text-sm hidden sm:table-cell">{l.contact_name || '--'}</TableCell>
                <TableCell className="text-xs hidden md:table-cell">{l.contact_email || '--'}</TableCell>
                <TableCell className="hidden lg:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {(l.specialties || []).map((s, i) => <Badge key={i} variant="outline" className="text-[10px] border-gray-300 text-gray-700">{s}</Badge>)}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(l)}><Pencil className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-400" onClick={() => handleDelete(l.id)}><Trash2 className="w-3 h-3" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900" data-testid="lender-dialog">
          <DialogHeader><DialogTitle className="text-gray-900 uppercase">{editId ? 'Edit Lender' : 'Add Lender'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <Input placeholder="Lender Name *" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-gray-100 border-gray-200" data-testid="lender-name" />
            <Input placeholder="Address" value={form.address || ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="bg-gray-100 border-gray-200" data-testid="lender-address" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Contact Name" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} className="bg-gray-100 border-gray-200" />
              <Input placeholder="Contact Phone" value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} className="bg-gray-100 border-gray-200" />
            </div>
            <Input placeholder="Contact Email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} className="bg-gray-100 border-gray-200" />
            <Input placeholder="Specialties (comma separated)" value={form.specialties} onChange={e => setForm(f => ({ ...f, specialties: e.target.value }))} className="bg-gray-100 border-gray-200" />
            <Textarea placeholder="Submission Requirements" value={form.requirements} onChange={e => setForm(f => ({ ...f, requirements: e.target.value }))} className="bg-gray-100 border-gray-200" rows={3} />
            <Textarea placeholder="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="bg-gray-100 border-gray-200" rows={2} />
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E31937] text-white" disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Add Lender'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <BulkActionBar count={selected.length} onDelete={async () => {
        if (!window.confirm(`Delete ${selected.length} lender(s)?`)) return;
        try { await lendersAPI.bulkDelete(selected); toast.success(`${selected.length} lender(s) deleted`); setSelected([]); load(); } catch { toast.error('Failed'); }
      }} onClear={() => setSelected([])} label="lender(s)" />
    </div>
  );
}
